import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, differenceInDays } from 'date-fns';
import { Calendar as CalendarIcon, UploadCloud, Info, AlertTriangle, ArrowRight, CheckCircle2, ChevronRight, File as FileIcon, X, XCircle } from 'lucide-react';

import { leaveTypeApi, leaveApi, hierarchyApi, userApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Input, Textarea, Select } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ValidationResult, ApprovalChainStep } from '@/types';

const applySchema = z.object({
  leaveTypeId: z.string().min(1, 'Please select a leave type'),
  fromDate: z.date({ message: 'Start date is required' }),
  toDate: z.date({ message: 'End date is required' }),
  isHalfDay: z.boolean().optional().default(false),
  halfDayPeriod: z.enum(['MORNING', 'AFTERNOON']).optional(),
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
  handoverNote: z.string().optional(),
  handoverPersonId: z.string().optional(),
});

type ApplyForm = z.infer<typeof applySchema>;

export default function ApplyLeavePage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [file, setFile] = useState<File | null>(null);
  
  // Pre-submission state
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [approvalPath, setApprovalPath] = useState<ApprovalChainStep[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: leaveTypesResponse } = useQuery({
    queryKey: ['leave-types'],
    queryFn: leaveTypeApi.getAll,
  });
  const leaveTypes = leaveTypesResponse?.data?.data || [];

  const { data: usersResponse } = useQuery({
    queryKey: ['users-active'],
    queryFn: userApi.getActive,
  });
  const users = usersResponse?.data?.data?.filter((u: any) => u.id !== user?.id) || [];

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
  } = useForm<ApplyForm>({
    resolver: zodResolver(applySchema) as any,
    defaultValues: { isHalfDay: false },
  });

  const watchLeaveTypeId = watch('leaveTypeId');
  const watchFromDate = watch('fromDate');
  const watchToDate = watch('toDate');
  const watchIsHalfDay = watch('isHalfDay');

  const selectedLeaveType = leaveTypes.find((lt: any) => lt.id === watchLeaveTypeId);

  // Validate on the fly when dates/type change
  useEffect(() => {
    if (watchLeaveTypeId && watchFromDate && watchToDate) {
      if (watchFromDate <= watchToDate) {
        setIsValidating(true);
        leaveApi.validate({
          leaveTypeId: watchLeaveTypeId,
          fromDate: watchFromDate.toISOString(),
          toDate: watchToDate.toISOString(),
          isHalfDay: watchIsHalfDay,
        })
          .then((res) => setValidation(res.data.data))
          .catch(console.error)
          .finally(() => setIsValidating(false));
      }
    } else {
      setValidation(null);
    }
  }, [watchLeaveTypeId, watchFromDate, watchToDate, watchIsHalfDay]);

  const handleNextToStep2 = async () => {
    const valid = await trigger();
    if (!valid) return;
    if (validation?.errors?.length) {
      toast.error('Please fix the validation errors before proceeding');
      return;
    }
    
    // Fetch approval path
    try {
      const res = await hierarchyApi.getApprovalPath(user!.id, watchLeaveTypeId);
      setApprovalPath(res.data.data);
      if (res.data.data.length === 0) {
        toast.error('No approval chain configured for your role. Contact HR.');
        return;
      }
      setStep(2);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to calculate approval path. Contact your admin to assign a manager or hierarchy level.');
    }
  };

  const onSubmit = async (data: ApplyForm) => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('leaveTypeId', data.leaveTypeId);
      formData.append('fromDate', data.fromDate.toISOString());
      formData.append('toDate', data.toDate.toISOString());
      formData.append('isHalfDay', data.isHalfDay.toString());
      if (data.halfDayPeriod) formData.append('halfDayPeriod', data.halfDayPeriod);
      formData.append('reason', data.reason);
      if (data.handoverNote) formData.append('handoverNote', data.handoverNote);
      if (data.handoverPersonId) formData.append('handoverPersonId', data.handoverPersonId);
      if (file) formData.append('document', file);

      await leaveApi.apply(formData);
      setStep(3); // Success step
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress header */}
      <div className="mb-6 mt-2">
        <div className="flex items-start justify-between relative">
          <div className="absolute left-0 top-4 w-full h-1 bg-slate-200 z-0 rounded-full -translate-y-1/2"></div>
          <div 
            className="absolute left-0 top-4 h-1 bg-blue-600 z-0 transition-all duration-500 rounded-full -translate-y-1/2" 
            style={{ width: step === 1 ? '0%' : step === 2 ? '50%' : '100%' }}
          ></div>
          
          {[1, 2, 3].map((num) => (
            <div key={num} className={`relative z-10 flex flex-col items-center ${step >= num ? 'text-blue-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 transition-colors ${step >= num ? 'bg-white border-blue-600 shadow-sm' : 'bg-slate-50 border-slate-200 text-sm'}`}>
                {step > num ? <CheckCircle2 className="w-4 h-4 text-blue-600" /> : num}
              </div>
              <span className="text-xs font-semibold mt-2 text-center whitespace-nowrap">
                {num === 1 ? 'Leave Details' : num === 2 ? 'Approval Preview' : 'Confirmation'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        
        {step === 1 && (
          <div className="p-4 sm:p-5 animate-fade-in">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Leave Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-4">
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Leave Type</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {leaveTypes.map((lt: any) => (
                      <label 
                        key={lt.id} 
                        className={`relative flex items-center justify-center px-3 py-3 border rounded-xl cursor-pointer transition-all ${watchLeaveTypeId === lt.id ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500 shadow-sm' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                      >
                        <input type="radio" value={lt.id} {...register('leaveTypeId')} className="sr-only" />
                        <div className="flex flex-col items-center gap-1">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: lt.colour }}></span>
                          <span className="text-xs font-semibold text-slate-700">{lt.code}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {errors.leaveTypeId && <p className="mt-1 text-sm text-red-600">{errors.leaveTypeId.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    control={control}
                    name="fromDate"
                    render={({ field }) => (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-slate-700 mb-1">From Date</label>
                        <div className="border border-slate-300 rounded-md p-2 relative bg-white">
                          <CalendarIcon className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                          <input 
                            type="date" 
                            className="w-full pl-8 outline-none text-sm text-slate-700" 
                            onChange={(e) => field.onChange(new Date(e.target.value))}
                            value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
                          />
                        </div>
                        {errors.fromDate && <p className="mt-1 text-xs text-red-600">{errors.fromDate.message}</p>}
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name="toDate"
                    render={({ field }) => (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-slate-700 mb-1">To Date</label>
                        <div className="border border-slate-300 rounded-md p-2 relative bg-white">
                          <CalendarIcon className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                          <input 
                            type="date" 
                            className="w-full pl-8 outline-none text-sm text-slate-700" 
                            onChange={(e) => field.onChange(new Date(e.target.value))}
                            value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
                            min={watchFromDate ? format(watchFromDate, 'yyyy-MM-dd') : ''}
                          />
                        </div>
                        {errors.toDate && <p className="mt-1 text-xs text-red-600">{errors.toDate.message}</p>}
                      </div>
                    )}
                  />
                </div>

                {watchFromDate && watchToDate && watchFromDate.getTime() === watchToDate.getTime() && (
                  <div className="flex items-center gap-4 bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input type="checkbox" {...register('isHalfDay')} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4" />
                      Half Day
                    </label>
                    {watchIsHalfDay && (
                      <Select {...register('halfDayPeriod')} className="py-1 text-sm h-8">
                        <option value="MORNING">Morning (First Half)</option>
                        <option value="AFTERNOON">Afternoon (Second Half)</option>
                      </Select>
                    )}
                  </div>
                )}

                <Textarea
                  label="Reason for Leave"
                  placeholder="Provide a brief reason..."
                  rows={3}
                  {...register('reason')}
                  error={errors.reason?.message}
                />

              </div>

              {/* Right Col: Validation & File Upload */}
              <div className="flex flex-col gap-5 h-full">
                <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 flex-1 flex flex-col">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-600" /> 
                    Application Summary
                  </h3>
                  
                  {!watchLeaveTypeId || !watchFromDate || !watchToDate ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-8 text-slate-400">
                      <CalendarIcon className="w-8 h-8 mb-2 opacity-50" />
                      <p className="text-sm">Select dates and leave type to see calculation</p>
                    </div>
                  ) : isValidating ? (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : validation ? (
                    <div className="space-y-4 flex-1">
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-slate-200">
                        <span className="text-sm text-slate-600">Working Days</span>
                        <span className="text-xl font-bold text-slate-800">{validation.workingDays}</span>
                      </div>
                      
                      {validation.errors.length > 0 && (
                        <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                          <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <XCircle className="w-3 h-3" /> Errors ({validation.errors.length})
                          </h4>
                          <ul className="list-disc pl-4 space-y-1">
                            {validation.errors.map((e, i) => (
                              <li key={i} className="text-sm text-red-700">{e}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {validation.warnings.length > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                          <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Warnings ({validation.warnings.length})
                          </h4>
                          <ul className="list-disc pl-4 space-y-1">
                            {validation.warnings.map((w, i) => (
                              <li key={i} className="text-sm text-amber-700">{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {validation.valid && validation.warnings.length === 0 && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-100 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                          <p className="text-sm text-green-800 font-medium">Valid leave request. Good to go!</p>
                        </div>
                      )}
                    </div>
                  ) : null}

                  {selectedLeaveType?.requiresDocAfterDays && validation && validation.workingDays >= selectedLeaveType.requiresDocAfterDays && (
                    <div className="mt-4 p-4 bg-white rounded-lg border-2 border-dashed border-blue-300">
                      <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                        <UploadCloud className="w-4 h-4 text-blue-600" />
                        Medical Certificate Required
                      </h4>
                      <p className="text-xs text-slate-500 mb-3">
                        {selectedLeaveType.name} longer than {selectedLeaveType.requiresDocAfterDays} days requires supporting documents.
                      </p>
                      <input 
                        type="file" 
                        id="doc-upload"
                        className="hidden" 
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      <label htmlFor="doc-upload" className="flex items-center justify-center w-full py-2 bg-blue-50 text-blue-600 text-sm font-medium rounded cursor-pointer hover:bg-blue-100 transition-colors">
                        {file ? file.name : 'Select File'}
                      </label>
                    </div>
                  )}

                </div>
                
                {/* Handover Details */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    Handover Details <span className="text-slate-400 font-normal text-sm">(Optional)</span>
                  </h3>
                  <Select {...register('handoverPersonId')} label="Handing over to">
                    <option value="">-- Select Person --</option>
                    {users.map((u: any) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.designation})</option>
                    ))}
                  </Select>
                  <Textarea
                    placeholder="Brief handover notes or instructions..."
                    rows={2}
                    {...register('handoverNote')}
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end pt-6 border-t border-slate-100">
              <Button 
                onClick={handleNextToStep2} 
                size="lg"
                disabled={!validation?.valid || isValidating}
              >
                Proceed to Preview <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-6 sm:p-8 animate-fade-in">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold text-slate-800">Approval Preview</h2>
              <Button variant="ghost" onClick={() => setStep(1)} size="sm">Edit Details</Button>
            </div>

            {(() => {
              const manager = approvalPath[0];
              return (
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 mb-8 text-center max-w-md mx-auto">
                  <p className="text-slate-600 text-sm mb-4">Your leave request will be sent to your manager for approval.</p>
                  <div className="flex items-center justify-center gap-4">
                    {/* Applicant */}
                    <div className="flex flex-col items-center">
                      <div className="w-14 h-14 bg-white rounded-full border-2 border-slate-300 flex items-center justify-center mb-2 shadow-sm">
                        {user?.photoUrl ? <img src={user.photoUrl} className="w-full h-full rounded-full object-cover" /> : <span className="font-bold text-slate-500">You</span>}
                      </div>
                      <span className="text-xs font-semibold text-slate-700">Applicant</span>
                    </div>
                    {/* Connector */}
                    <div className="w-12 h-1 bg-slate-300 rounded-full mb-5"></div>
                    {/* Manager */}
                    <div className="flex flex-col items-center">
                      <div className="w-14 h-14 bg-blue-50 rounded-full border-2 border-blue-500 flex items-center justify-center mb-2 shadow-sm">
                        {manager?.approver?.photoUrl ? (
                          <img src={manager.approver.photoUrl} title={manager.approver.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-xs font-bold text-blue-700">{manager?.approver?.name?.split(' ')[0] ?? 'N/A'}</span>
                        )}
                      </div>
                      <span className="text-xs font-semibold text-slate-700">{manager?.approver?.name ?? 'Not Assigned'}</span>
                      <span className="text-[10px] text-slate-500">Manager</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-between pt-6 border-t border-slate-100">
              <Button variant="secondary" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleSubmit(onSubmit)} isLoading={isSubmitting} size="lg">
                Confirm & Submit Application
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="p-12 text-center animate-fade-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Leave Applied Successfully!</h2>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">
              Your application has been routed to your manager for approval. You will be notified once they take action.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button variant="secondary" onClick={() => { setStep(1); trigger(); }}>Apply Another</Button>
              <Button onClick={() => navigate('/leaves/my')}>View My Leaves</Button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
